/**
 * The product card's choices (snippets/oracura-shop-card.liquid): a colour
 * dot or a model button (LITE / PRO) picks the variant that has both, and the
 * card follows it — the pressed dot and button, the price, MRP, discount on
 * the photo and beside the price, the photograph in that colour, the link
 * (it opens the product page on that variant) and Add to Bag.
 *
 * One listener for every card on the page, wherever it is drawn, so a grid
 * that loads more cards or a section the theme editor redraws needs nothing.
 *
 * The collection filter presses the model buttons itself and then reads the
 * button's data-pct for a discount sort; the buttons' figures are kept to the
 * colour shown, so what it reads is what the card shows.
 */
(() => {
  const variantsOf = (card) => {
    if (!card.cardVariants) {
      try {
        card.cardVariants = JSON.parse(card.querySelector('[data-card-variants]')?.textContent || '[]');
      } catch (error) {
        card.cardVariants = [];
      }
    }
    return card.cardVariants;
  };

  const chosen = (card) => {
    const picked = {};
    card.querySelectorAll('[data-card-option][aria-pressed="true"]').forEach((button) => {
      picked[button.dataset.cardOption] = button.dataset.value;
    });
    return picked;
  };

  // A card that leaves an option to the product page (a tank size, a pack):
  // it carries data-card-choose-card, and its price is the lowest of the
  // variants it stands for.
  const choosing = (card) => card.hasAttribute('data-card-choose-card');

  // The cheapest of a list, the first of equals (the product's own order).
  const cheapest = (list) => list.reduce((low, v) => (low === null || v.c < low.c ? v : low), null);

  // The variant with this value and everything else already chosen, in stock
  // if there is one; failing that, this value's first in stock, then its first.
  // On a Choose Options card the size is not the card's to pick, so it is the
  // cheapest in stock in that colour (as the card opened), not the first.
  const resolve = (card, index, value) => {
    const variants = variantsOf(card);
    const picked = { ...chosen(card), [index]: value };
    const fits = (variant) => Object.entries(picked).every(([i, v]) => variant.o[i] === v);
    const has = (variant) => variant.o[index] === value;
    if (choosing(card)) {
      for (const test of [fits, has]) {
        const pool = variants.filter(test);
        const found = cheapest(pool.filter((v) => v.a)) || cheapest(pool);
        if (found) return found;
      }
      return null;
    }
    return variants.find((v) => fits(v) && v.a) || variants.find(fits)
      || variants.find((v) => has(v) && v.a) || variants.find(has) || null;
  };

  const show = (element, text) => {
    if (!element) return;
    element.hidden = !text;
    if (text) element.textContent = text;
  };

  function apply(card, variant) {
    const { mrpLabel = '', suffix = '', addLabel = '', soldoutLabel = '' } = card.dataset;
    // The corner badge's floor (the snippet's badge_min): a smaller saving
    // keeps its struck MRP but gets no gold badge.
    const badgeMin = Number(card.dataset.badgeMin) || 0;

    card.querySelectorAll('[data-card-option]').forEach((button) => {
      button.setAttribute('aria-pressed', String(variant.o[button.dataset.cardOption] === button.dataset.value));
    });

    // Each model button's figures, for the colour now shown.
    const variants = variantsOf(card);
    const colourButton = card.querySelector('.oracura-card__colour');
    const colourIndex = colourButton ? colourButton.dataset.cardOption : null;
    card.querySelectorAll('[data-plp-model]').forEach((button) => {
      const i = button.dataset.cardOption;
      const same = (v) => v.o[i] === button.dataset.value && (colourIndex === null || v.o[colourIndex] === variant.o[colourIndex]);
      const own = variants.find(same) || variants.find((v) => v.o[i] === button.dataset.value && v.a) || variants.find((v) => v.o[i] === button.dataset.value);
      if (!own) return;
      button.dataset.variantId = own.id;
      button.dataset.price = own.p;
      button.dataset.mrp = own.m || '';
      button.dataset.pct = own.pct ? String(own.pct) : '';
      button.dataset.available = String(own.a);
    });

    // One variant, one price: the "From" goes. A Choose Options card keeps it
    // while the sizes in the colour shown cost different amounts, since the
    // page it opens still asks for one.
    const from = card.querySelector('[data-card-from]');
    if (from && choosing(card)) {
      const own = Array.from(new Set(Array.from(card.querySelectorAll('[data-card-option]'), (b) => b.dataset.cardOption)));
      const prices = new Set(variants.filter((v) => own.every((i) => v.o[i] === variant.o[i])).map((v) => v.c));
      from.hidden = prices.size < 2;
    } else {
      from?.remove();
    }
    const now = card.querySelector('[data-card-now]');
    if (now) now.textContent = variant.p;
    show(card.querySelector('[data-card-mrp]'), variant.m ? `${mrpLabel} ${variant.m}`.trim() : '');
    show(card.querySelector('[data-sale-badge]'), variant.pct && variant.pct >= badgeMin ? `${variant.pct}% ${suffix}`.trim() : '');
    show(card.querySelector('[data-card-chip]'), variant.pct ? `${variant.pct}% ${suffix}`.trim() : '');

    // The card's link and its Choose Options button open the product page on
    // the variant shown: a colour picked here is the colour the page opens on.
    const link = card.querySelector('[data-card-link]');
    if (link) {
      const url = new URL(link.href, location.href);
      url.searchParams.set('variant', variant.id);
      link.href = url.pathname + url.search;
      card.querySelectorAll('[data-card-choose]').forEach((choose) => {
        choose.href = link.href;
      });
    }

    // In stock: Add to Bag for it. Sold out: Notify Me for it.
    const add = card.querySelector('[data-plp-add]');
    const notify = card.querySelector('[data-card-notify]');
    if (add) {
      add.dataset.plpAdd = variant.id;
      add.hidden = !variant.a && Boolean(notify);
      add.disabled = !variant.a;
      const text = add.querySelector('[data-card-add-text]');
      if (text) text.textContent = variant.a || notify ? addLabel : soldoutLabel;
    }
    if (notify) {
      notify.hidden = variant.a;
      notify.dataset.variantId = variant.id;
      notify.dataset.variantTitle = variant.t && variant.t !== 'Default Title' ? variant.t : '';
      notify.disabled = false;
      notify.classList.remove('is-notified');
      const label = notify.querySelector('[data-notify-text]');
      if (label && notify.dataset.label) label.textContent = notify.dataset.label;
      if (label && !notify.dataset.label) notify.dataset.label = label.textContent;
      if (window.OracuraNotify?.isNotified(variant.id)) window.OracuraNotify.markDone(notify);
    }

    // A colour with nothing to buy in the model now shown is drawn sold out.
    const modelButton = card.querySelector('[data-plp-model]');
    const modelIndex = modelButton ? modelButton.dataset.cardOption : null;
    card.querySelectorAll('.oracura-card__colour').forEach((dot) => {
      const i = dot.dataset.cardOption;
      const stock = variants.some((v) => v.a && v.o[i] === dot.dataset.value && (modelIndex === null || v.o[modelIndex] === variant.o[modelIndex]));
      dot.classList.toggle('is-soldout', !stock);
      const note = dot.querySelector('[data-soldout-note]');
      if (note) note.hidden = stock;
    });

    // The photograph in the colour chosen, where the card has one.
    const dot = card.querySelector('.oracura-card__colour[aria-pressed="true"]');
    const img = card.querySelector('.oracura-card__img');
    const photo = variant.i || dot?.dataset.photo;
    if (img && photo && img.getAttribute('src') !== photo) {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = photo;
    }
    if (dot && dot.dataset.fill) card.querySelector('.oracura-card__media')?.style.setProperty('--card-fill', dot.dataset.fill);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.oracura-card [data-card-option]');
    if (!button) return;
    event.preventDefault();
    const card = button.closest('.oracura-card');
    if (!card || card.querySelector('[aria-busy="true"]')) return;
    const variant = resolve(card, button.dataset.cardOption, button.dataset.value);
    if (variant) apply(card, variant);
  });
})();
